import PropTypes from 'prop-types';
import React from 'react';
import get from 'lodash/get';
import {
  Button,
  Checkbox,
  SingleSelectionGroup,
  TextInput,
  LoadingSpinner,
} from '@paypalcorp/pp-react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import * as analytics from '../lib/analytics';
import parseAmount from '../lib/parseAmount';
import { isPPGF } from '../lib/charity';
import classNames from 'classnames';
import styles from './donateBox.less';
import { DataContext } from '../lib/context';
import { TrackingImpressionPixel } from './TrackingImpressionPixel';

function ButtonWrapper({ children }) {
  const [{ isPending }] = usePayPalScriptReducer();

  // Pass status to the received _render prop_
  return children(isPending);
}

export default class extends React.Component {
  static contextType = DataContext;
  static contextTypes = {
    csrf: PropTypes.string,
  };

  static defaultProps = {
    active: true,
  };

  constructor(props) {
    super(props);
    let nonanonymous = true;
    if (
      get(props, 'charity.address.country_code') === 'GB' ||
      props.isGroupDonation
    ) {
      nonanonymous = false;
    }
    if (props.amounts.tracking) {
      this.tracking = props.amounts.tracking.impression_url;
    }

    this.state = {
      nonanonymous,
      recurring: false,
      selectedAmount: '',
      clickedIndex: -1,
      inputAmount: '',
      radioBtnClicked: false,
      errorPopup: false,
      nemoTag: props.isGroupDonation ? 'gtg' : 'charity',
      showOtherOption: false,
      isButtonReady: false,
      isLoadingConfirmation: false,
    };
  }

  handleNonAnonymousCheck = (event) => {
    this.setState(
      Object.assign({}, this.state, {
        nonanonymous: event.target.checked,
      })
    );
  };

  handleInputChange = (event) => {
    const value = event.target.value;
    let input = parseAmount(value, this.context.griffin.number);
    if (input === undefined) {
      return this.invalid();
    }

    this.setState({
      inputAmount: input,
    });
  };

  invalid = () => {
    this.setState({
      isInvalid: true,
    });

    setTimeout(() => {
      this.setState({
        isInvalid: false,
      });
    }, 2000);
  };

  handleDonateClick = (event, isGtg, npid, venmoButtonActions) => {
    if (
      this.state.inputAmount === '' &&
      (!this.state.radioBtnClicked || this.state.selectedAmount === 'other')
    ) {
      if (!venmoButtonActions) {
        event.preventDefault();
      }

      this.setState({
        errorPopup: true,
      });

      setTimeout(() => {
        this.setState({
          errorPopup: false,
        });
      }, 2000);

      if (venmoButtonActions) {
        return venmoButtonActions.reject();
      }
    }

    analytics.trackLinkWithData(
      `${isGtg ? 'gtgdonateButton' : 'donateButton'}`,
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      {
        amount: this.state.selectedAmount || this.state.inputAmount,
        preset_y_n: this.state.selectedAmount !== '' ? 'y' : 'n',
        nonanonymous: this.state.nonanonymous,
        crid: npid,
      },
      'link'
    );
  };

  handleAmountSelection = (event, isGtg) => {
    if (this.props.amounts.tracking) {
      this.tracking = this.props.amounts.tracking.click_url;
    }
    this.setState({
      selectedAmount: event.target.value,
      radioBtnClicked: true,
      inputAmount: '',
      showOtherOption: event.target.value === 'other',
    });
    analytics.trackLinkWithData(
      `${isGtg ? 'gtgAmtSelect' : 'donateAmtSelect'}`,
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { amount: event.target.value },
      'link'
    );
  };

  handleKeyPressCheckBox(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.setState({
        nonanonymous: !this.state.nonanonymous,
      });
    }
  }

  donationForm = () => {
    let nonprofit_id,
      charityType,
      charityName,
      formUrl,
      captureUrl,
      anonymousLabel,
      donateText;
    if (this.props.charity) {
      anonymousLabel = this.props.charityContent
        ? this.props.i18n(this.props.charityContent.anonymousCheck)
        : this.props.anonymousLabel;

      donateText = this.props.i18n('label.donate-now');
      nonprofit_id = this.props.charity.nonprofit_id;
      charityType = this.props.charity.charity_type;
      charityName = this.props.charity.name;
      formUrl = this.props.formUrl;
      captureUrl = this.props.captureUrl;
    } else {
      nonprofit_id = this.props.charityDetails.nonprofit_id;
      charityType = this.props.charityDetails.charity_type;
      charityName = this.props.charityDetails.name;
      formUrl = `${this.props.coBrand}charity/${nonprofit_id}/xo/create`;
      captureUrl = `${this.props.coBrand}charity/${nonprofit_id}/xo/execute`;
      anonymousLabel = this.props.i18n('label.make-nonanonymous-enrolled');
      donateText = this.props.donateButtonText
        ? this.props.donateButtonText
        : this.props.i18n('label.donate-button');
    }
    const nonAnonymousInput = (
      <Checkbox
        checked={this.state.nonanonymous}
        label={anonymousLabel}
        className={styles.checkBox}
        onChange={this.handleNonAnonymousCheck}
        onKeyDown={(e) => this.handleKeyPressCheckBox(e)}
        name='nonanonymous-checkbox'
      />
    );

    let name;
    if (isPPGF(charityType)) {
      name = this.props.i18n('label.ppgf-donation-description', {
        charity: this.props.isGroupDonation
          ? this.props.virtualCharity.name
          : charityName,
      });
    } else {
      name = this.props.i18n('label.donation-description', {
        charity: this.props.isGroupDonation
          ? this.props.virtualCharity.name
          : charityName,
      });
    }

    let charityNpid = this.props.isGroupDonation
      ? this.props.charity.groupVirtualNpid
      : nonprofit_id;

    const ShowPayPalButton = () => (
      <Button
        className={styles.donateButton}
        disabled={!this.props.enable}
        type={'submit'}
        onClick={(e) =>
          this.handleDonateClick(e, this.props.isGroupDonation, charityNpid)
        }
        id={`nemo_donatebutton_${this.state.nemoTag}`}
      >
        {donateText}
      </Button>
    );

    return (
      <form
        action={formUrl}
        method={'post'}
        className={styles.formStyle}
        id={`nemo_donation_form_${this.state.nemoTag}`}
      >
        <input
          type={'hidden'}
          name={'_csrf'}
          value={this.props.csrf || this.context.csrf}
        />
        <input type={'hidden'} name={'npid'} value={charityNpid} />
        <input
          type={'hidden'}
          name={'eventUrl'}
          value={this.props.eventUrl || 'eventUrl'}
        />
        <input type={'hidden'} name={'name'} value={name} />
        <input
          type={'hidden'}
          name={'nonanonymous'}
          value={this.state.nonanonymous}
        />
        <input
          type={'hidden'}
          name={'currency'}
          value={this.props.amounts.currencyCode}
        />
        <input
          type={'hidden'}
          name={'amount'}
          value={
            this.state.inputAmount
              ? this.state.inputAmount
              : this.state.selectedAmount
          }
        />
        {isPPGF(charityType) && !this.props.isGroupDonation
          ? nonAnonymousInput
          : null}
        {this.props.campaignInfo && this.props.campaignInfo.isVenmo ? (
          <div style={{ marginTop: '24px' }}>
            <ButtonWrapper>
              {(isPending) => {
                if (isPending || this.state.isLoadingConfirmation) {
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <LoadingSpinner size='md' />
                    </div>
                  );
                } else {
                  return (
                    <PayPalButtons
                      disabled={!this.props.enable || !this.state.isButtonReady}
                      fundingSource='venmo'
                      style={{
                        shape: 'pill',
                      }}
                      onClick={(data, actions) =>
                        this.handleDonateClick(
                          null,
                          this.props.isGroupDonation,
                          charityNpid,
                          actions
                        )
                      }
                      onInit={() => {
                        this.setState({ isButtonReady: true });
                      }}
                      createOrder={() => {
                        const form = new FormData(
                          document.getElementById('nemo_donation_form_charity')
                        );

                        // Request no redirection,
                        // so that we can have a response body with the order ID
                        form.set('redirect', 'false');

                        return fetch(formUrl, { method: 'POST', body: form })
                          .then(function (res) {
                            return res.json();
                          })
                          .then(function (orderData) {
                            return orderData.id;
                          });
                      }}
                      onApprove={(data, actions) => {
                        const executeURL = new URL(window.location.origin);
                        executeURL.pathname = captureUrl;

                        const search = new URLSearchParams(executeURL.search);
                        search.append('capture', 'true');
                        search.append('token', data.orderID);
                        search.append('PayerID', data.payerID);
                        executeURL.search = search;

                        // Don't follow redirections,
                        // but reload page to load flash messages
                        return fetch(executeURL.toString(), {
                          redirect: 'manual',
                        }).then(() => {
                          this.setState({ isLoadingConfirmation: true });
                          window.location.reload();
                        });
                      }}
                    >
                      <ShowPayPalButton />
                    </PayPalButtons>
                  );
                }
              }}
            </ButtonWrapper>
          </div>
        ) : (
          <ShowPayPalButton />
        )}
      </form>
    );
  };

  render() {
    const inputBoxStyling = classNames(styles.textInputMargin, {
      showError: this.state.errorPopup,
    });
    const currencyCode = this.props.amounts.currencyCode;
    const otherAmountLabel = this.props.i18n('label.other-amount');
    const otherAmountPlaceholder = `${otherAmountLabel} (${currencyCode})`;

    let selectionOptions = this.props.amounts.options.map((option, index) => {
      return {
        label: option.formatted,
        value: option.value,
        className: styles.donateOptions,
      };
    });

    selectionOptions = [
      ...selectionOptions,
      {
        label: otherAmountLabel,
        value: 'other',
        className: styles.donateOptions,
      },
    ];

    return (
      <div className={styles.donationSelection}>
        {this.tracking ? <TrackingImpressionPixel src={this.tracking} /> : null}
        <SingleSelectionGroup
          id={this.props.id}
          errorText={
            !this.state.showOtherOption &&
            this.state.errorPopup &&
            this.props.i18n('label.input-error')
          }
          name={`amounts_${this.state.nemoTag}`}
          align='justify'
          value={this.state.selectedAmount}
          onChange={(e) =>
            this.handleAmountSelection(e, this.props.isGroupDonation)
          }
          options={selectionOptions}
        />
        {this.state.showOtherOption && (
          <TextInput
            name='help'
            type={'text'}
            id={`nemo_inputAmount_${this.state.nemoTag}`}
            errorText={
              this.state.errorPopup && this.props.i18n('label.input-error')
            }
            helperText={
              this.state.isInvalid && this.props.i18n('label.invalid-input')
            }
            className={`${inputBoxStyling}`}
            onChange={this.handleInputChange}
            value={this.state.inputAmount}
            label={otherAmountPlaceholder}
            autoComplete={'off'}
            required
            ref={(input) => input && input.focus()}
          />
        )}
        {this.donationForm()}
      </div>
    );
  }
}
